import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, ExternalLink, Calendar, Code, MapPin, Search, Users, DollarSign } from 'lucide-react';
import { Input } from '@/components/ui/input';

const CompetitionsPage = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('hackathons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[Competitions] Supabase error:', error.message, error.details, error.hint);
        return;
      }

      console.log('[Competitions] Fetched', data?.length || 0, 'entries');
      setCompetitions(data || []);
    } catch (error) {
      console.error('[Competitions] Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompetitions = competitions.filter(comp =>
    comp.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    comp.platform?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    comp.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatPrize = (prize) => {
    if (!prize || prize === 0) return null;
    if (prize >= 1000) return `$${(prize / 1000).toFixed(0)}K`;
    return `$${prize}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-primary to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Trophy className="w-7 h-7" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl">
                Competitions & Hackathons
              </h1>
              <p className="text-indigo-100">Test your skills in real-world challenges</p>
            </div>
          </div>
          <p className="text-indigo-100 text-lg max-w-2xl mb-8">
            Discover the latest hackathons, coding challenges, and competitions updated daily from across the globe.
          </p>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input
              placeholder="Search competitions, platforms, tags..."
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus-visible:ring-white/30"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-64 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredCompetitions.length === 0 ? (
          <div className="text-center py-20 bg-slate-50 rounded-2xl border border-dashed">
            <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No competitions found</h3>
            <p className="text-slate-500">We couldn't find any competitions matching your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompetitions.map((comp) => (
              <Card key={comp.id} className="overflow-hidden hover:shadow-lg transition-all group flex flex-col">
                {comp.thumbnail_url && (
                  <div className="h-48 overflow-hidden bg-slate-100">
                    <img
                      src={comp.thumbnail_url}
                      alt={comp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&q=80';
                      }}
                    />
                  </div>
                )}
                <CardHeader>
                  <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                      <Code className="w-3 h-3 mr-1" /> Hackathon
                    </Badge>
                    {comp.is_active && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Active
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="line-clamp-2 text-xl">{comp.title}</CardTitle>
                  {comp.tags && comp.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {comp.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="mt-auto pt-4 border-t">
                  <div className="flex flex-col gap-3">
                    {comp.deadline && (
                      <div className="flex items-center text-sm text-slate-600">
                        <Calendar className="w-4 h-4 mr-2 shrink-0" />
                        <span>Deadline: {new Date(comp.deadline).toLocaleDateString()}</span>
                      </div>
                    )}
                    {comp.location && (
                      <div className="flex items-center text-sm text-slate-600">
                        <MapPin className="w-4 h-4 mr-2 shrink-0" />
                        <span className="line-clamp-1">{comp.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      {comp.prize_usd > 0 && (
                        <div className="flex items-center text-sm text-green-600 font-medium">
                          <DollarSign className="w-4 h-4 mr-1" />
                          <span>{formatPrize(comp.prize_usd)}</span>
                        </div>
                      )}
                      {comp.participants > 0 && (
                        <div className="flex items-center text-sm text-slate-500">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{comp.participants.toLocaleString()}</span>
                        </div>
                      )}
                      {comp.platform && (
                        <Badge variant="outline" className="text-xs ml-auto">
                          {comp.platform}
                        </Badge>
                      )}
                    </div>
                    <Button className="w-full mt-2" variant="outline" asChild>
                      <a href={comp.source_url || '#'} target="_blank" rel="noopener noreferrer">
                        View Details <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitionsPage;
